import { useState, useEffect } from 'react';
import { makeGetCall } from '../utils/helper';

/**
 * Custom hook to fetch active modality locations (admin-managed master that
 * maps a location name to the DICOM AE Title its machines send with).
 * Options are de-duplicated by location name — one location may have several
 * AE Title entries, but the PACS filter dropdown lists each name once.
 * @returns {Object} { locationOptions, loading, error, refetch }
 */
export const useModalityLocations = () => {
  const [locationOptions, setLocationOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchModalityLocations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await makeGetCall('/modality-locations');

      if (response?.data?.success) {
        const names = [...new Set((response.data.data || []).map(loc => loc.location))];
        setLocationOptions(names.map(name => ({ label: name, value: name })));
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch modality locations');
      }
    } catch (err) {
      console.error('Error fetching modality locations:', err);
      setError(err.message || 'Failed to fetch modality locations');
      setLocationOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModalityLocations();
  }, []);

  const refetch = () => {
    fetchModalityLocations();
  };

  return {
    locationOptions,
    loading,
    error,
    refetch
  };
};
